import type {
  Candidate,
  AfcSubmissionPosition,
  AfcSubmissionCandidateOpportunity,
  AfcSubmission,
  AfcSubmissionCandidate,
} from '@axiom/validation';
import { CurrencyUtil } from '@axiom/utils';
import type { CurrencyCodeValueTypes } from '@axiom/const';
import { CandidateOpportunitiesConst } from '@axiom/const';

const { hourly, daily, weekly } =
  CandidateOpportunitiesConst.DisplayBillingRate;
const { DisplayBillingRate } = CandidateOpportunitiesConst;

const RateIncrement = {
  DAY: 'day',
  HOUR: 'hour',
  WEEK: 'week',
} as const;

// Create a type from the values
type RateIncrementType = (typeof RateIncrement)[keyof typeof RateIncrement];

const getRateObject = ({
  currencyCode,
  price,

  increment,
}: {
  currencyCode: CurrencyCodeValueTypes | null;
  price: number;
  increment: RateIncrementType;
}) => {
  return {
    rate: CurrencyUtil.getFormattedCurrency({ value: price, currencyCode }),
    increment,
  };
};

const getRate = ({
  proposedHourlyRate,
  displayBillingRate,
  billingHoursPerDay,
  currencyCode,
}: {
  proposedHourlyRate?: number | null;
  displayBillingRate?:
    (typeof DisplayBillingRate)[keyof typeof DisplayBillingRate] | null;
  billingHoursPerDay: number | null;
  currencyCode: CurrencyCodeValueTypes | null;
}) => {
  let price = proposedHourlyRate;
  if (!price || price < 0) {
    return '--';
  }

  const hoursPerDay = billingHoursPerDay ?? 0;

  switch (displayBillingRate) {
    case hourly: {
      return getRateObject({
        currencyCode,
        price,
        increment: RateIncrement.HOUR,
      });
    }
    case daily: {
      if (hoursPerDay > 0) {
        price *= hoursPerDay;
        return getRateObject({
          currencyCode,
          price,
          increment: RateIncrement.DAY,
        });
      }
      break;
    }
    case weekly: {
      if (hoursPerDay > 0) {
        price *= hoursPerDay * 5;
        return getRateObject({
          currencyCode,
          price,
          increment: RateIncrement.WEEK,
        });
      }
      break;
    }
    default: {
      return '--';
    }
  }

  return '--';
};

export const CandidateProposedRateUtil = {
  formatCandidateRate(candidateId: Candidate['id'], submission: AfcSubmission) {
    let displayBillingRate:
      AfcSubmissionCandidateOpportunity['displayBillingRate'] | null = null;
    let proposedHourlyRate:
      AfcSubmissionCandidateOpportunity['proposedHourlyRate'] | null = null;

    let billingHoursPerDay: AfcSubmissionCandidate['hoursPerDay'] = null;

    submission.positions.find((pos: AfcSubmissionPosition) => {
      const data = pos.candidateOpportunities.find(
        (co: AfcSubmissionCandidateOpportunity) =>
          co.candidate.id === candidateId
      );

      if (data) {
        displayBillingRate = data.displayBillingRate;
        proposedHourlyRate = data.proposedHourlyRate;
        billingHoursPerDay = data.candidate.hoursPerDay;
      }

      return !!data;
    });

    return getRate({
      proposedHourlyRate,
      displayBillingRate,
      billingHoursPerDay,
      currencyCode: submission.currency,
    });
  },
  shouldDisplayRate(candidateOpportunity: AfcSubmissionCandidateOpportunity) {
    return !!(
      candidateOpportunity.displayBillingRate &&
      candidateOpportunity.displayBillingRate !==
        CandidateOpportunitiesConst.DisplayBillingRate.none &&
      candidateOpportunity.proposedHourlyRate
    );
  },
};
